import { useEffect } from 'react';
import * as v from 'valibot';

import { createFileRoute, stripSearchParams } from '@tanstack/react-router';

import { faGhost } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Container, Typography } from '@mui/material';

import { ClaimType } from '@repo/api/claim/claims.types';
import { useUser } from '@repo/auth';

import { saveHomeSearchSnapshot } from '../../-context/homeSearchStorage';
import { ClaimsTable } from './-components/ClaimsTable';
import { ClaimStats } from './-components/ClaimStats';

const defaultFilters = {
	page: 0,
	rowsPerPage: 10,
	policyReferences: [],
	claimsStatusIds: [],
	claimsHandlerIds: [],
	searchText: '',
	showClosed: false,
};

export const Filters = v.object({
	page: v.optional(v.number(), defaultFilters.page),
	rowsPerPage: v.optional(v.number(), defaultFilters.rowsPerPage),
	claimType: v.optional(ClaimType),
	policyReferences: v.optional(
		v.fallback(v.array(v.string()), defaultFilters.policyReferences),
	),
	claimsStatusIds: v.optional(
		v.fallback(v.array(v.string()), defaultFilters.claimsStatusIds),
	),
	claimsHandlerIds: v.optional(
		v.fallback(v.array(v.string()), defaultFilters.claimsHandlerIds),
	),
	fromCreatedDate: v.optional(v.string()),
	toCreatedDate: v.optional(v.string()),
	fromDateOfLoss: v.optional(v.string()),
	toDateOfLoss: v.optional(v.string()),
	searchText: v.optional(v.string(), defaultFilters.searchText),
	showClosed: v.optional(v.fallback(v.boolean(), defaultFilters.showClosed)),
});

export const Route = createFileRoute('/_claims/claims/')({
	component: function HomePage() {
		const { permissions } = useUser();
		const search = Route.useSearch();

		useEffect(() => {
			saveHomeSearchSnapshot('claims', search);
		}, [search]);

		const canViewDashboard = permissions.includes('home.claim.dashboard');
		const canViewClaimsTable = permissions.includes('home.claim.table');

		return (
			<>
				{canViewDashboard && (
					<Container
						disableGutters
						maxWidth="lg"
						sx={{
							mt: 2,
						}}
					>
						<ClaimStats />
					</Container>
				)}
				{canViewClaimsTable && (
					<Container
						maxWidth="lg"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							backgroundColor: 'offwhite.light',
							borderRadius: 2,
							paddingTop: 2,
							marginTop: 2,
						}}
					>
						<Typography variant="h5">All Claims</Typography>
						<ClaimsTable />
					</Container>
				)}
				{!canViewDashboard && !canViewClaimsTable && (
					<Container
						maxWidth="lg"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							backgroundColor: 'offwhite.light',
							pt: 2,
							mt: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								p: 4,
								gap: 1,
								position: 'relative',
								m: 'auto',
							}}
						>
							<FontAwesomeIcon color="#cccccc" icon={faGhost} size="2xl" />
						</Box>
					</Container>
				)}
			</>
		);
	},
	validateSearch: Filters,
	search: {
		middlewares: [stripSearchParams(defaultFilters)],
	},
	head: (ctx) => ctx.match.context.setTitle('Claims', 'Home'),
});
